// ComposeEmailModal — send email via the user's connected Outlook (Graph).
// Self-contained overlay. Preflights /me/outlook/status so a user who isn't
// connected or isn't send-enabled sees why BEFORE composing. No silent failure:
// a failed send surfaces the server's reason inline.
import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";

const IDENTITY_LABEL = { self: "My address", accounting: "accounting@waltersnorthwest.com", admin: "adminsupport@waltersnorthwest.com" };

// Post-send confirmation body — shared by ComposeEmailModal + InvoiceEmailModal
// (Kuhrt 2026-07-05: every send, from anywhere, confirms status / contents /
// recipients / attachments before the modal goes away).
export function EmailSentConfirmation({ info, onDone }) {
  const Row = ({ label, children }) => !children ? null : (
    <div style={{ display: "flex", gap: 10, marginBottom: 7, fontSize: 13 }}>
      <span style={{ width: 64, flexShrink: 0, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text3)", paddingTop: 2 }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{children}</span>
    </div>);
  return (
    <>
      <div style={{ padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#dcfce7", color: "#166534", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14, fontWeight: 600 }}>
          <span style={{ fontSize: 17 }}>✓</span>
          <span>Email sent{info.claimNumber ? ` — filed on claim ${info.claimNumber}` : ""}</span>
        </div>
        <Row label="From">{info.from}</Row>
        <Row label="To">{info.to}</Row>
        <Row label="Cc">{info.cc}</Row>
        <Row label="Bcc">{info.bcc}</Row>
        <Row label="Subject">{info.subject}</Row>
        <Row label="Attached">{info.attachments && info.attachments.length > 0 ? (
          <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {info.attachments.map((a, i) => <span key={i}>📎 {a.name}</span>)}
          </span>) : null}</Row>
        {info.body ? (
          <div style={{ marginTop: 10, border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 12.5, color: "var(--text2,#555)", whiteSpace: "pre-wrap", maxHeight: 220, overflowY: "auto", background: "var(--bg3,#f8fafc)" }}>
            {info.body}
          </div>) : null}
      </div>
      <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-primary btn-sm" onClick={onDone}>Done</button>
      </div>
    </>
  );
}

export default function ComposeEmailModal({ claimId, claim, prefill, onClose, onSent }) {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState(prefill?.to || "");
  const [showCc, setShowCc] = useState(false);
  const [cc, setCc] = useState(prefill?.cc || "");
  const [bcc, setBcc] = useState(prefill?.bcc || "");
  const [subject, setSubject] = useState(prefill?.subject || "");
  const [body, setBody] = useState(prefill?.body || "");
  const [identity, setIdentity] = useState("self");
  const [appendSig, setAppendSig] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [selDocs, setSelDocs] = useState(new Set(prefill?.document_ids || []));
  const [showDocs, setShowDocs] = useState(false);
  const [docSearch, setDocSearch] = useState("");
  const [expandedFolders, setExpandedFolders] = useState(null); // null = all open
  const [files, setFiles] = useState([]);   // uploaded-from-computer attachments (base64)
  const [sentInfo, setSentInfo] = useState(null);  // set on success → confirmation view
  const fileRef = useRef(null);

  useEffect(() => {
    apiFetch("/me/outlook/status").then(setStatus).catch(() => setStatus({ connected: false }));
    if (claimId) {
      apiFetch(`/claims/${claimId}/email-recipients`).then(r => setRecipients(r || [])).catch(() => {});
      apiFetch(`/claims/${claimId}/documents`).then(d => setDocs(d || [])).catch(() => {});
    }
  }, [claimId]);

  const ready = status && status.connected && status.email_send_enabled;

  // append an email to a To/Cc/Bcc field (comma-joined), avoiding dupes
  const addRecip = (field, email) => {
    const setter = { to: setTo, cc: setCc, bcc: setBcc }[field];
    const cur = { to, cc, bcc }[field];
    const parts = cur.split(",").map(s => s.trim()).filter(Boolean);
    if (parts.map(p => p.toLowerCase()).includes(email.toLowerCase())) return;
    setter([...parts, email].join(", "));
    if (field !== "to") setShowCc(true);
  };
  const toggleDoc = (id) => setSelDocs(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const fmtBytes = (b) => !b ? "" : b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : `${Math.round(b/1024)} KB`;
  const onPickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    chosen.forEach(f => {
      const r = new FileReader();
      r.onload = () => {
        const b64 = String(r.result).split(",")[1] || "";
        setFiles(prev => [...prev, { name: f.name, content_type: f.type || "application/octet-stream", content_b64: b64, size: f.size }]);
      };
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };
  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const send = async () => {
    setMsg(null);
    if (!to.trim()) { setMsg({ type: "error", text: "Add at least one recipient." }); return; }
    setSending(true);
    // plain text → HTML (escape + newlines)
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const body_html = esc(body).replace(/\n/g, "<br>");
    try {
      const r = await apiFetch("/email/send", { method: "POST", body: {
        to, cc: showCc ? cc : "", bcc: showCc ? bcc : "", subject, body_html,
        claim_id: claimId || null, sending_identity: identity, append_signature: appendSig,
        document_ids: [...selDocs],
        attachments: files.map(f => ({ name: f.name, content_type: f.content_type, content_b64: f.content_b64 })),
      }});
      // Confirmation view replaces the compose form; onSent/onClose fire when
      // the user dismisses it (so parents that close-on-sent don't hide it).
      setSentInfo({
        id: r?.id,
        from: identity === "self" ? (status?.email || "my address") : (IDENTITY_LABEL[identity] || identity),
        to, cc: showCc ? cc : "", bcc: showCc ? bcc : "", subject, body,
        attachments: [
          ...docs.filter(d => selDocs.has(d.id)).map(d => ({ name: d.original_filename })),
          ...files.map(f => ({ name: f.name })),
        ],
        claimNumber: claim?.claim_number || null,
      });
      setSending(false);
    } catch (e) {
      let d = "Email could not be sent."; try { d = JSON.parse(e.message).detail || d; } catch {}
      setMsg({ type: "error", text: d });
      setSending(false);
    }
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "48px 16px", overflowY: "auto" };
  const sheet = { background: "var(--surface2,#fff)", borderRadius: 12, width: "100%", maxWidth: 640, boxShadow: "0 10px 40px rgba(0,0,0,0.25)" };
  const lbl = { fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3, display: "block" };
  const inp = { width: "100%", boxSizing: "border-box" };

  if (sentInfo) {
    const finish = () => { if (onSent) onSent(sentInfo); if (onClose) onClose(); };
    return (
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) finish(); }}>
        <div style={sheet}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Email sent</h3>
            <button className="btn btn-outline btn-sm" onClick={finish}>✕</button>
          </div>
          <EmailSentConfirmation info={sentInfo} onDone={finish} />
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={sheet}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>New email{claim?.claim_number ? ` · ${claim.claim_number}` : ""}</h3>
          <button className="btn btn-outline btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          {status && !status.connected && (
            <div className="alert-error" style={{ marginBottom: 12 }}>
              Your Outlook isn't connected. Connect it in Profile → Outlook email before sending.
            </div>
          )}
          {status && status.connected && !status.email_send_enabled && (
            <div className="alert-error" style={{ marginBottom: 12 }}>
              Email sending isn't enabled for your account. Ask an administrator to enable it.
            </div>
          )}
          {msg && <div className={msg.type === "error" ? "alert-error" : ""} style={msg.type === "success" ? { background: "#dcfce7", color: "#166534", padding: "8px 12px", borderRadius: 6, marginBottom: 12 } : { marginBottom: 12 }}>{msg.text}</div>}

          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>From</label>
            <select value={identity} onChange={(e) => setIdentity(e.target.value)} style={inp} disabled={!ready}>
              {["self", ...(status?.shared_identities || [])].filter((v, i, a) => a.indexOf(v) === i).map(k => (
                <option key={k} value={k}>{IDENTITY_LABEL[k] || k}{k === "self" && status?.email ? ` (${status.email})` : ""}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>To</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com, another@example.com" style={inp} disabled={!ready} />
          </div>
          {recipients.length > 0 && (
            <div style={{ marginBottom: 10, padding: "8px 10px", background: "var(--bg3,#f8fafc)", borderRadius: 6 }}>
              <div style={{ ...lbl, marginBottom: 6 }}>Slip parties</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {recipients.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <strong>{r.role}</strong> · {r.name} <span style={{ color: "var(--text3)" }}>&lt;{r.email}&gt;</span>
                    </span>
                    {["to", "cc", "bcc"].map(f => (
                      <button key={f} className="btn btn-outline" style={{ padding: "1px 8px", fontSize: 11, textTransform: "uppercase", ...(r.suggested === f ? { borderColor: "var(--navy,#000066)", fontWeight: 700 } : {}) }}
                              onClick={() => addRecip(f, r.email)} disabled={!ready} title={`Add to ${f.toUpperCase()}`}>{f}</button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!showCc && <button className="btn btn-link btn-sm" style={{ padding: 0, marginBottom: 8 }} onClick={() => setShowCc(true)}>+ Cc / Bcc</button>}
          {showCc && (
            <>
              <div style={{ marginBottom: 10 }}><label style={lbl}>Cc</label><input value={cc} onChange={(e) => setCc(e.target.value)} style={inp} disabled={!ready} /></div>
              <div style={{ marginBottom: 10 }}><label style={lbl}>Bcc</label><input value={bcc} onChange={(e) => setBcc(e.target.value)} style={inp} disabled={!ready} /></div>
            </>
          )}
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} style={inp} disabled={!ready} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Message</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} disabled={!ready} />
          </div>
          <div style={{ marginBottom: 12, border: "1px solid var(--border)", borderRadius: 8, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Attachments{(selDocs.size + files.length) > 0 ? ` · ${selDocs.size + files.length}` : ""}</label>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {claimId && <button className="btn btn-outline btn-sm" onClick={() => setShowDocs(s => !s)} disabled={!ready} title="Attach documents already on this claim">📄 From documents</button>}
                <button className="btn btn-outline btn-sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={!ready} title="Attach files from your computer">💻 From computer</button>
                <input ref={fileRef} type="file" multiple onChange={onPickFiles} style={{ display: "none" }} />
              </div>
            </div>

            {/* selected attachments (docs + uploads) as removable chips */}
            {(selDocs.size > 0 || files.length > 0) && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                {docs.filter(d => selDocs.has(d.id)).map(d => (
                  <div key={`d${d.id}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, background: "var(--bg3,#f8fafc)", padding: "4px 8px", borderRadius: 4 }}>
                    <span style={{ fontSize: 11 }}>📄</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.original_filename}</span>
                    <span style={{ color: "var(--text3)", fontSize: 11 }}>{fmtBytes(d.bytes_stored)}</span>
                    <button className="btn btn-link" style={{ padding: 0, color: "#b91c1c" }} onClick={() => toggleDoc(d.id)} title="Remove">✕</button>
                  </div>
                ))}
                {files.map((f, i) => (
                  <div key={`f${i}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, background: "var(--bg3,#f8fafc)", padding: "4px 8px", borderRadius: 4 }}>
                    <span style={{ fontSize: 11 }}>💻</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <span style={{ color: "var(--text3)", fontSize: 11 }}>{fmtBytes(f.size)}</span>
                    <button className="btn btn-link" style={{ padding: 0, color: "#b91c1c" }} onClick={() => removeFile(i)} title="Remove">✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* hierarchical, searchable document picker */}
            {showDocs && claimId && (
              <div style={{ marginTop: 8, border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
                <input value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search this claim's documents…"
                       style={{ width: "100%", boxSizing: "border-box", border: "none", borderBottom: "1px solid var(--border)", padding: "7px 10px", fontSize: 12.5 }} />
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {docs.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, color: "var(--text3)" }}>No documents on this claim yet.</div>
                  ) : (() => {
                    const q = docSearch.trim().toLowerCase();
                    const shown = docs.filter(d => !q || (d.original_filename || "").toLowerCase().includes(q));
                    if (shown.length === 0) return <div style={{ padding: 12, fontSize: 12, color: "var(--text3)" }}>No matches.</div>;
                    const groups = {};
                    shown.forEach(d => { const k = d.folder_name || "Unfiled"; (groups[k] = groups[k] || []).push(d); });
                    return Object.keys(groups).sort().map(folder => {
                      const open = q || expandedFolders == null || expandedFolders.has(folder);
                      return (
                        <div key={folder}>
                          <div onClick={() => setExpandedFolders(prev => { const n = new Set(prev == null ? Object.keys(groups) : prev); n.has(folder) ? n.delete(folder) : n.add(folder); return n; })}
                               style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "var(--bg2,#f6f6f4)", cursor: "pointer", fontSize: 11.5, fontWeight: 600, color: "var(--navy)", position: "sticky", top: 0 }}>
                            <span style={{ fontSize: 9, color: "var(--text3)" }}>{open ? "▾" : "▸"}</span>📁 {folder}
                            <span style={{ marginLeft: "auto", color: "var(--text3)", fontWeight: 400 }}>{groups[folder].length}</span>
                          </div>
                          {open && groups[folder].map(d => (
                            <label key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px 5px 22px", fontSize: 12.5, borderTop: "1px solid var(--border)", cursor: "pointer" }}>
                              <input type="checkbox" checked={selDocs.has(d.id)} onChange={() => toggleDoc(d.id)} disabled={!ready} />
                              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.original_filename}</span>
                              <span style={{ color: "var(--text3)", fontSize: 11 }}>{fmtBytes(d.bytes_stored)}</span>
                            </label>
                          ))}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 6 }}>Attachments total up to 25 MB.</div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>
            <input type="checkbox" checked={appendSig} onChange={(e) => setAppendSig(e.target.checked)} disabled={!ready} />
            Append my signature
          </label>
          {claimId && <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>This email will be filed on the claim (PHI-audited).</div>}
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={send} disabled={sending || !ready}>{sending ? "Sending…" : "Send"}</button>
        </div>
      </div>
    </div>
  );
}
